import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { usePortal } from './PortalContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalLogin = () => {
  const { login } = usePortal();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Bienvenido');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/auth/request-password`, { email: email.trim() });
      toast.success('Si el email existe, te enviamos un correo para configurar tu contraseña');
      setMode('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar el correo');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={`${process.env.PUBLIC_URL || ''}/fex-logo-light.png`}
            alt="FEX Fitness Experience"
            className="h-16 w-auto mx-auto mb-3 select-none"
            draggable="false"
          />
          <p className="text-xs uppercase tracking-widest text-zinc-500">Portal del Socio</p>
        </div>

        <Card className="bg-[#0A0A0A] border-zinc-800">
          <CardContent className="p-6">
            {mode === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="portal-login-form">
                <div>
                  <Label className="text-xs text-zinc-500 uppercase">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      required
                      className="pl-10 bg-zinc-900 border-zinc-700"
                      data-testid="portal-login-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500 uppercase">Contraseña</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="pl-10 bg-zinc-900 border-zinc-700"
                      data-testid="portal-login-password"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-login-submit">
                  {submitting ? 'Accediendo…' : 'Entrar'} <ArrowRight size={16} className="ml-2" />
                </Button>
                <div className="flex justify-between text-xs pt-2">
                  <button type="button" onClick={() => setMode('forgot')} className="text-zinc-400 hover:text-emerald-400" data-testid="portal-forgot-link">
                    ¿Olvidaste tu contraseña?
                  </button>
                  <Link to="/admin/login" className="text-zinc-500 hover:text-zinc-300">Soy Staff</Link>
                </div>
              </form>
            ) : (
              <form onSubmit={handleForgot} className="space-y-4" data-testid="portal-forgot-form">
                <p className="text-sm text-zinc-400">
                  Ingresa tu email y te enviaremos un enlace para configurar o restablecer tu contraseña.
                </p>
                <div>
                  <Label className="text-xs text-zinc-500 uppercase">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      required
                      className="pl-10 bg-zinc-900 border-zinc-700"
                      data-testid="portal-forgot-email"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-forgot-submit">
                  {submitting ? 'Enviando…' : 'Enviar enlace'}
                </Button>
                <button type="button" onClick={() => setMode('login')} className="w-full text-xs text-zinc-400 hover:text-emerald-400">
                  ← Volver al login
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PortalLogin;
